/** core libraries & third party packages */
import React, { useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import LocomotiveScroll from "locomotive-scroll";
import "locomotive-scroll/dist/locomotive-scroll.css";

/** component */
import Home from "./pages/home/Home";
import Navbar from "./components/common/navbar/Navbar";
import Cart from "./pages/cart/Cart";
import Shop from "./pages/shop/Shop";
import Products from "./pages/products/Products";
import ProductDetails from "./pages/product-details/ProductDetails";
import Footer from "./components/common/footer/Footer";
import Profile from "./pages/Profile/Profile";
import AboutUs from "./pages/about-us/AboutUs";
import CreateProduct from "./pages/productOperation/CreateProduct";
import PublicRoutes from "./components/core/routes/PublicRoutes";
import ProtectedRoutes from "./components/core/routes/ProtectedRoutes";
import AccountDetails from "./components/core/profile/AccountDetails";
import Wishlist from "./components/core/profile/Wishlist";
import Order from "./components/core/profile/Order";
import Address from "./components/core/profile/Address";
import Checkout from "./pages/checkout/Checkout";
import OrderComplete from "./pages/order-complete/OrderComplete";
import ErrorPage from "./pages/not-found/ErrorPage";

/** utils */
import { handleGetUserFromLocalStorage } from "./utils/common";

/** interfaces */
import { IUser } from "./Interfaces/user";

const App: React.FC = () => {
  const currentUser = JSON.parse(
    handleGetUserFromLocalStorage() as string
  ) as IUser;

  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!scrollRef.current) return;

    const scroll = new LocomotiveScroll({
      el: scrollRef.current,
      smooth: true,
      lerp: 2.08,
    } as any);

    return () => scroll.destroy();
  }, []);

  return (
    <Router>
      <div ref={scrollRef}>
        <Navbar />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="*" element={<ErrorPage />} />

          <Route
            path="/products"
            element={
              <PublicRoutes>
                <Products />
              </PublicRoutes>
            }
          />
          <Route
            path="/product-details/:productId"
            element={
              <PublicRoutes>
                <ProductDetails />
              </PublicRoutes>
            }
          />
          <Route
            path="/about-us"
            element={
              <PublicRoutes>
                <AboutUs />
              </PublicRoutes>
            }
          />
          <Route
            path="/shop"
            element={
              <PublicRoutes>
                <Shop />
              </PublicRoutes>
            }
          />
          <Route
            path="/cart"
            element={
              <ProtectedRoutes>
                <Cart />
              </ProtectedRoutes>
            }
          />
          <Route
            path="/checkout"
            element={
              <ProtectedRoutes>
                <Checkout />
              </ProtectedRoutes>
            }
          />
          <Route
            path="/order-complete"
            element={
              <ProtectedRoutes>
                <OrderComplete />
              </ProtectedRoutes>
            }
          />

          <Route
            element={
              <ProtectedRoutes>
                <Profile />
              </ProtectedRoutes>
            }
          >
            <Route
              path="/profile"
              element={
                <ProtectedRoutes>
                  <Navigate to="/profile/account-details" />
                </ProtectedRoutes>
              }
            />

            <Route
              path="/profile/account-details"
              element={
                <ProtectedRoutes>
                  <AccountDetails />
                </ProtectedRoutes>
              }
            />
            <Route
              path="/profile/wishlist"
              element={
                <ProtectedRoutes>
                  <Wishlist />
                </ProtectedRoutes>
              }
            />
            <Route
              path="/profile/orders"
              element={
                <ProtectedRoutes>
                  <Order />
                </ProtectedRoutes>
              }
            />
            <Route
              path="/profile/address"
              element={
                <ProtectedRoutes>
                  <Address />
                </ProtectedRoutes>
              }
            />
          </Route>

          {/* product operation only admin can access  */}
          {currentUser && currentUser.role.includes("admin") && (
            <Route path="/product/create-product" element={<CreateProduct />} />
          )}
        </Routes>
        <Footer />
      </div>
    </Router>
  );
};

export default App;
